import React, { useState, useEffect } from 'react';
import {
  Typography,
  Card,
  Slider,
  Button,
  Space,
  Divider,
  Modal,
  message,
  Checkbox,
  Row,
  Col,
  InputNumber,
  Table,
  Breadcrumb,
} from 'antd';
import {
  FireOutlined,
  ShoppingCartOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { getUserProfile } from '../auth';
import { api } from '../api';

const { Title, Text, Paragraph } = Typography;

// 1クレジット = 15円
const CREDIT_PRICE = 15;

// スライダーのマーク
const sliderMarks = {
  100: '100',
  500: '500',
  1000: '1,000',
  1500: '1,500',
  2000: '2,000',
};

const BonusCreditPurchase = () => {
  const [userProfile, setUserProfile] = useState(null);
  const [selectedCredits, setSelectedCredits] = useState(100);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      const profile = await getUserProfile();
      setUserProfile(profile);
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      message.error('ユーザー情報の取得に失敗しました');
    }
  };

  // 金額計算（税込）
  const calculatePrice = credits => {
    const subtotal = credits * CREDIT_PRICE;
    const tax = Math.floor(subtotal * 0.1);
    return {
      subtotal,
      tax,
      total: subtotal + tax,
    };
  };

  const priceInfo = calculatePrice(selectedCredits);

  const handleSliderChange = value => {
    setSelectedCredits(value);
  };

  const handleInputChange = value => {
    if (value >= 10 && value <= 2000) {
      setSelectedCredits(value);
    }
  };

  const handlePurchaseClick = () => {
    setConfirmModalVisible(true);
    setIsConfirmed(false);
  };

  const confirmPurchase = async () => {
    setIsLoading(true);
    try {
      const response = await api.post('/credits/purchase-bonus', {
        credit_amount: selectedCredits,
      });

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        message.error('決済ページの取得に失敗しました');
      }
    } catch (error) {
      console.error('Failed to initiate purchase:', error);
      message.error(
        'ボーナスクレジットの購入に失敗しました: ' +
          (error.response?.data?.detail || error.message),
      );
    } finally {
      setIsLoading(false);
    }
  };

  // 金額テーブルのデータ
  const priceTableData = [
    {
      key: 'credits',
      label: '購入ボーナスクレジット数',
      value: (
        <Text strong style={{ color: '#0072ff', fontSize: '18px' }}>
          <FireOutlined style={{ marginRight: '6px' }} />
          {selectedCredits.toLocaleString()}
        </Text>
      ),
    },
    {
      key: 'subtotal',
      label: '税抜金額',
      value: `¥${priceInfo.subtotal.toLocaleString()}`,
    },
    {
      key: 'tax',
      label: '消費税（10%）',
      value: `¥${priceInfo.tax.toLocaleString()}`,
    },
    {
      key: 'total',
      label: 'お支払い金額（税込）',
      value: (
        <Text strong style={{ color: '#fa8c16', fontSize: '20px' }}>
          ¥{priceInfo.total.toLocaleString()}
        </Text>
      ),
    },
  ];

  const priceTableColumns = [
    {
      dataIndex: 'label',
      key: 'label',
      width: '50%',
    },
    {
      dataIndex: 'value',
      key: 'value',
      width: '50%',
      align: 'right',
    },
  ];

  return (
    <>
      <Helmet>
        <title>ボーナスクレジット購入 | magicss</title>
        <meta name="robots" content="noindex" />
        <meta property="og:title" content="ボーナスクレジット購入 | magicss" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={window.location.href} />
        <meta
          property="og:image"
          content={`${process.env.PUBLIC_URL}/img/ogp.png`}
        />
        <meta
          property="og:description"
          content="コンテンツ生成AIツール「magicss」のボーナスクレジット購入ページです。"
        />
      </Helmet>
      <div>
        <Breadcrumb
          style={{ marginBottom: 16 }}
          items={[
            { title: <Link to="/mypage">マイページ</Link> },
            { title: 'ボーナスクレジット購入' },
          ]}
        />
        <Title level={1} className="gradient-text" style={{ fontSize: '24px', marginBottom: 8 }}>
          ボーナスクレジット購入
        </Title>
        <Text type="secondary">
          追加でボーナスクレジットを購入できます。購入したクレジットは即時反映されます。
        </Text>
        <Divider style={{ margin: '24px 0' }} />

        {/* メインコンテンツ：2カラム */}
        <Row gutter={[32, 24]}>
          {/* 左カラム：スライダー */}
          <Col xs={24} lg={12}>
            <Card
              title={
                <Space>
                  <ShoppingCartOutlined style={{ color: '#0072ff' }} />
                  <Text strong>購入するクレジット数を選択</Text>
                </Space>
              }
            >
              <div style={{ padding: '16px 0' }}>
                <Slider
                  min={10}
                  max={2000}
                  step={10}
                  marks={sliderMarks}
                  value={selectedCredits}
                  onChange={handleSliderChange}
                  tooltip={{
                    formatter: value => `${value.toLocaleString()} クレジット`,
                  }}
                  styles={{
                    track: { backgroundColor: '#0072ff' },
                    handle: { borderColor: '#0072ff' },
                  }}
                />
              </div>

              <div style={{ marginTop: '24px' }}>
                <Text style={{ marginRight: '12px' }}>または直接入力：</Text>
                <InputNumber
                  min={10}
                  max={2000}
                  step={10}
                  value={selectedCredits}
                  onChange={handleInputChange}
                  style={{ width: '180px' }}
                  formatter={value =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                  }
                  parser={value => value.replace(/,/g, '')}
                />
                <Text style={{ marginLeft: '8px' }}>クレジット</Text>
              </div>

              <div style={{ marginTop: '16px' }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  ※ ボーナスクレジットは1クレジット = {CREDIT_PRICE}
                  円（税抜）で計算されます
                </Text>
              </div>
            </Card>
          </Col>

          {/* 右カラム：金額表示 */}
          <Col xs={24} lg={12}>
            <Card title="お支払い内容">
              <Table
                dataSource={priceTableData}
                columns={priceTableColumns}
                pagination={false}
                showHeader={false}
                size="middle"
                style={{ marginBottom: '24px' }}
              />

              <Button
                type="primary"
                size="large"
                icon={<ShoppingCartOutlined />}
                onClick={handlePurchaseClick}
                loading={isLoading}
                block
                style={{
                  background:
                    'linear-gradient(90deg, #0072ff 0%, #00d4ff 100%)',
                  border: 'none',
                  height: '50px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                }}
              >
                ボーナスクレジットを購入する
              </Button>
            </Card>
          </Col>
        </Row>

        {/* 注意事項 */}
        <Card
          title={
            <Space>
              <InfoCircleOutlined style={{ color: '#1890ff' }} />
              <Text strong>ご購入に関する注意事項</Text>
            </Space>
          }
          style={{ marginTop: '24px' }}
        >
          <ul style={{ paddingLeft: '20px', margin: 0 }}>
            <li>購入したボーナスクレジットは即時反映されます</li>
            <li>ボーナスクレジットの有効期限はありません（無期限）</li>
            <li>クレジット消費時は、月間クレジットが優先して消費されます</li>
            <li>購入後の返金・キャンセルはお受けできません</li>
            <li>決済はStripeを通じて安全に処理されます</li>
            <li>領収書はStripeから自動送信されます</li>
          </ul>
        </Card>

        {/* 確認モーダル */}
        <Modal
          title={<Title level={3}>ご購入内容の確認</Title>}
          open={confirmModalVisible}
          onOk={confirmPurchase}
          onCancel={() => setConfirmModalVisible(false)}
          okText="決済に進む"
          cancelText="キャンセル"
          width={500}
          okButtonProps={{
            disabled: !isConfirmed,
            loading: isLoading,
            style: {
              background: isConfirmed
                ? 'linear-gradient(90deg, #0072ff 0%, #00d4ff 100%)'
                : undefined,
              border: 'none',
            },
          }}
        >
          <Table
            dataSource={priceTableData}
            columns={priceTableColumns}
            pagination={false}
            showHeader={false}
            size="middle"
            style={{ marginBottom: '16px' }}
          />

          <Paragraph type="secondary">
            <InfoCircleOutlined style={{ marginRight: '8px' }} />
            決済完了後、ボーナスクレジットは即時付与されます。
          </Paragraph>

          <Divider />

          <Checkbox
            checked={isConfirmed}
            onChange={e => setIsConfirmed(e.target.checked)}
          >
            購入内容と注意事項を確認しました
          </Checkbox>
        </Modal>
      </div>
    </>
  );
};

export default BonusCreditPurchase;
